/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Copy, Eye, EyeOff, Link2, KeyRound, User, Webhook } from 'lucide-react';
import apiClient from '../../api/client';
import { accountService } from '../../services/accountService';
import toastService from '../../utils/toastService';
import Modal from '../Modal';
import PermissionGuard from '../PermissionGuard';

// Keep in sync with server/src/integrations/pixo-webhook/pixo-webhook.constants.ts
const TOKEN_MIN_LENGTH = 16;
const PASSWORD_MIN_LENGTH = 8;

const AUTH_OPTIONS = [
  { value: 'token', label: 'Authentication Token' },
  { value: 'basic', label: 'Basic Authentication' },
];

const EMPTY_FORM = { authType: 'token', token: '', username: '', password: '' };

// Same input style as the account form (EditAccount.jsx)
const inputClass = (hasError) =>
  `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${
    hasError ? 'border-error' : 'border-base-300'
  }`;

const errorMessage = (err, fallback) => {
  const msg = err.response?.data?.message;
  return Array.isArray(msg) ? msg[0] : msg || fallback;
};

/**
 * "User Sync Webhook" settings inside the Pixo Integration card of Edit Account.
 * The endpoint URL is not stored anywhere — it is built from the API base URL and the
 * account's saved slug. The token / Basic credentials are entered by hand (the same
 * values the admin enters in the Pixo panel); the server never returns them, so the
 * secret fields load empty and an empty value keeps the saved secret.
 * Saves on its own, separately from the account form.
 */
const PixoWebhookSettings = ({ accountId, accountSlug, pixoEnabled }) => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'authType' | 'disable'

  const webhookUrl = `${apiClient.defaults.baseURL}/integrations/pixo/webhook/${accountSlug}`;

  const applySettings = useCallback((data) => {
    setSettings(data);
    setForm({ ...EMPTY_FORM, authType: data.authType || 'token', username: data.basicUsername || '' });
    setErrors({});
    setShowSecret(false);
  }, []);

  useEffect(() => {
    if (!pixoEnabled) return;
    accountService
      .getPixoWebhook(accountId)
      .then(({ data }) => applySettings(data))
      .catch(() => toastService.error('Failed to load the Pixo webhook settings'));
  }, [accountId, pixoEnabled, applySettings]);

  const savedType = settings?.configured ? settings.authType : null;
  // The saved secret can be kept only while the auth type is unchanged
  const canKeepSecret = savedType === form.authType;

  const validate = () => {
    const next = {};
    if (form.authType === 'token') {
      const token = form.token.trim();
      if (!token && !canKeepSecret) next.token = 'Token is required';
      else if (token && token.length < TOKEN_MIN_LENGTH) next.token = `Token must be at least ${TOKEN_MIN_LENGTH} characters`;
      else if (/\s/.test(token)) next.token = 'Token must not contain spaces';
    } else {
      if (!form.username.trim()) next.username = 'Username is required';
      else if (form.username.includes(':')) next.username = 'Username must not contain ":"';
      if (!form.password && !canKeepSecret) next.password = 'Password is required';
      else if (form.password && form.password.length < PASSWORD_MIN_LENGTH) next.password = `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    setConfirm(null);
    try {
      setSaving(true);
      const body =
        form.authType === 'token'
          ? { authType: 'token', token: form.token.trim() }
          : { authType: 'basic', username: form.username.trim(), password: form.password };
      const { data } = await accountService.savePixoWebhook(accountId, body);
      applySettings(data);
      toastService.success('Pixo webhook saved');
    } catch (err) {
      toastService.error(errorMessage(err, 'Failed to save the Pixo webhook'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!validate()) return;
    // Switching the auth type breaks deliveries until Pixo is updated too
    if (savedType && savedType !== form.authType) setConfirm('authType');
    else save();
  };

  const disable = async () => {
    setConfirm(null);
    try {
      setSaving(true);
      const { data } = await accountService.disablePixoWebhook(accountId);
      applySettings(data);
      toastService.success('Pixo webhook disabled');
    } catch (err) {
      toastService.error(errorMessage(err, 'Failed to disable the Pixo webhook'));
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toastService.success('Webhook URL copied');
    } catch {
      toastService.error('Could not copy the URL');
    }
  };

  const setField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  };

  // The panel sits inside the account <form>: Enter must not submit the account form
  const preventFormSubmit = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault();
  };

  const labelClass = 'text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5';
  const keepPlaceholder = '•••••• (saved — leave blank to keep)';

  const secretInput = (field, placeholder) => (
    <div className="relative">
      <input
        id={`pixoWebhook-${field}`}
        type={showSecret ? 'text' : 'password'}
        autoComplete="new-password"
        maxLength={512}
        placeholder={canKeepSecret ? keepPlaceholder : placeholder}
        className={`${inputClass(errors[field])} pr-10 font-mono`}
        value={form[field]}
        onChange={(e) => setField(field, e.target.value)}
      />
      <button
        type="button"
        aria-label={showSecret ? 'Hide' : 'Show'}
        onClick={() => setShowSecret((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content"
      >
        {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );

  return (
    <PermissionGuard permissions={['accounts.update']}>
      <div className="pt-4 border-t border-base-content/10 space-y-4" onKeyDown={preventFormSubmit}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Webhook size={16} className="text-primary" />
            <h3 className="text-xs font-black text-base-content uppercase tracking-wider">User Sync Webhook</h3>
          </div>
          {pixoEnabled && settings && (
            <span className={`badge badge-sm font-bold ${settings.active ? 'badge-success' : 'badge-ghost'}`}>
              {settings.active ? 'Active' : settings.configured ? 'Disabled' : 'Not configured'}
            </span>
          )}
        </div>

        {!pixoEnabled ? (
          <p className="text-[11px] text-base-content/50">
            Save the account with Pixo Integration enabled to configure the user sync webhook.
          </p>
        ) : (
          <>
            <p className="text-[11px] text-base-content/40">
              Pixo creates and updates users of this account by calling this URL on UserCreated / UserUpdated.
              Enter the URL and the same credentials in the Pixo panel.
            </p>

            {/* Endpoint URL — derived from the saved slug, never stored */}
            <div className="space-y-1.5">
              <span className={labelClass}>
                <Link2 size={12} className="text-primary" />
                <span>Endpoint URL</span>
              </span>
              <div className="flex gap-2">
                <input
                  readOnly
                  aria-label="Pixo webhook endpoint URL"
                  className={`${inputClass(false)} font-mono text-xs bg-base-200/50`}
                  value={webhookUrl}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={copyUrl}
                  className="px-3 rounded-xl border border-base-300 text-base-content/70 hover:bg-base-200 transition flex items-center gap-1.5 text-xs font-bold"
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
              <p className="text-[11px] text-base-content/40">Changing the account slug changes this URL — update it in Pixo too.</p>
            </div>

            {/* Authentication */}
            <div className="space-y-2">
              <span className={labelClass}>
                <KeyRound size={12} className="text-primary" />
                <span>Authentication</span>
              </span>
              <div className="flex flex-wrap gap-4">
                {AUTH_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="pixoWebhookAuthType"
                      className="radio radio-primary radio-sm"
                      checked={form.authType === opt.value}
                      onChange={() => {
                        setForm((f) => ({ ...f, authType: opt.value, token: '', password: '' }));
                        setErrors({});
                      }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {form.authType === 'token' ? (
              <div className="space-y-1.5">
                <label htmlFor="pixoWebhook-token" className={labelClass}>
                  <KeyRound size={12} className="text-primary" />
                  <span>Token</span>
                </label>
                {secretInput('token', `At least ${TOKEN_MIN_LENGTH} characters`)}
                <p className="text-[11px] text-base-content/40">Pixo sends it in the x-access-webhook header.</p>
                {errors.token && <p className="text-error text-xs font-medium">{errors.token}</p>}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label htmlFor="pixoWebhook-username" className={labelClass}>
                    <User size={12} className="text-primary" />
                    <span>Username</span>
                  </label>
                  <input
                    id="pixoWebhook-username"
                    type="text"
                    autoComplete="off"
                    maxLength={255}
                    className={inputClass(errors.username)}
                    value={form.username}
                    onChange={(e) => setField('username', e.target.value)}
                  />
                  {errors.username && <p className="text-error text-xs font-medium">{errors.username}</p>}
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="pixoWebhook-password" className={labelClass}>
                    <KeyRound size={12} className="text-primary" />
                    <span>Password</span>
                  </label>
                  {secretInput('password', `At least ${PASSWORD_MIN_LENGTH} characters`)}
                  {errors.password && <p className="text-error text-xs font-medium">{errors.password}</p>}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-end gap-3">
              {settings?.updatedAt && (
                <span className="text-[11px] text-base-content/40 mr-auto">
                  Last updated {new Date(settings.updatedAt).toLocaleString()}
                </span>
              )}
              {settings?.active && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setConfirm('disable')}
                  className="px-4 py-2 border border-error/40 text-error rounded-xl font-bold text-xs hover:bg-error/10 transition disabled:opacity-50"
                >
                  Disable webhook
                </button>
              )}
              <button
                type="button"
                disabled={saving}
                onClick={handleSave}
                className="px-4 py-2 bg-primary text-primary-content rounded-xl font-bold text-xs hover:opacity-90 transition disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save webhook'}
              </button>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm === 'disable' ? 'Disable Pixo Webhook' : 'Change Authentication'}
      >
        <p className="mb-6 text-base-content/70 text-sm leading-relaxed">
          {confirm === 'disable'
            ? 'Pixo calls will be rejected and users will no longer be created or synced until the webhook is saved again.'
            : 'Pixo calls will be rejected until the same authentication is configured in the Pixo panel.'}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setConfirm(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm === 'disable' ? disable : save}
            className={`flex-1 px-4 py-2.5 rounded-xl font-bold text-sm hover:opacity-90 transition ${
              confirm === 'disable' ? 'bg-error text-error-content' : 'bg-primary text-primary-content'
            }`}
          >
            {confirm === 'disable' ? 'Disable' : 'Save'}
          </button>
        </div>
      </Modal>
    </PermissionGuard>
  );
};

PixoWebhookSettings.propTypes = {
  accountId: PropTypes.string.isRequired,
  /** Saved slug (not the one being edited) — the URL Pixo must call */
  accountSlug: PropTypes.string.isRequired,
  /** Whether Pixo is enabled in the saved account (not just toggled on in the form) */
  pixoEnabled: PropTypes.bool.isRequired,
};

export default PixoWebhookSettings;
